import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BRAND_BLUE = '#4841F0';
const STAGE_COLORS = {
  'Applied': '#94a3b8',
  'Phone Screen': '#60a5fa',
  'Technical Assessment': '#818cf8',
  'Interview': '#a78bfa',
  'Final Interview': '#c084fc',
  'Offer': '#f59e0b',
  'Hired': '#10b981',
  'Rejected': '#ef4444'
};

const tooltipStyle = {
  borderRadius: '8px',
  border: 'none',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  fontSize: '12px',
};

const ClientPipeline = ({ data, expanded = false }) => {
  const stageCounts = {};
  data.forEach(candidate => {
    const stage = candidate.stage || 'Unknown';
    stageCounts[stage] = (stageCounts[stage] || 0) + 1;
  });

  const stageOrder = ['Applied', 'Phone Screen', 'Technical Assessment', 'Interview', 'Final Interview', 'Offer', 'Hired', 'Rejected'];
  const chartData = stageOrder
    .filter(stage => stageCounts[stage] > 0)
    .map(stage => ({
      name: stage,
      count: stageCounts[stage] || 0,
      color: STAGE_COLORS[stage] || '#94a3b8'
    }));

  if (chartData.length === 0) {
    Object.entries(stageCounts).forEach(([stage, count]) => {
      chartData.push({ name: stage, count, color: STAGE_COLORS[stage] || '#94a3b8' });
    });
  }

  const total = data.length;
  const hired = stageCounts['Hired'] || 0;
  const rejected = stageCounts['Rejected'] || 0;
  const inProgress = total - hired - rejected;

  return (
    <div className="dashboard-card p-6 slide-in-up">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Candidate Pipeline</h3>
        <div className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-1 rounded-full">
          Pipeline Status
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-3">Current status of all candidates across pipeline stages</p>

      <div className="flex items-center space-x-4 text-xs mb-4">
        <span className="flex items-center">
          <span className="w-2 h-2 rounded-full bg-emerald-500 mr-1.5"></span>
          Hired: {hired}
        </span>
        <span className="flex items-center">
          <span className="w-2 h-2 rounded-full mr-1.5" style={{ background: BRAND_BLUE }}></span>
          In Progress: {inProgress}
        </span>
        <span className="flex items-center">
          <span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>
          Rejected: {rejected}
        </span>
      </div>

      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
          No pipeline data available yet
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={expanded ? 400 : 250}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              style={{ fontSize: '12px' }}
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value) => [`${value} candidates`, 'Count']}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={30}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}

      {expanded && chartData.length > 0 && (
        <div className="mt-6 border-t border-gray-100 pt-5">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Candidates by Stage</h4>
          <div className="space-y-3">
            {stageOrder.filter(stage => stageCounts[stage] > 0).map(stage => {
              const candidates = data.filter(c => c.stage === stage);
              return (
                <div key={stage} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="w-3 h-3 rounded-full mr-2" style={{ background: STAGE_COLORS[stage] || '#94a3b8' }}></span>
                      <span className="text-sm font-medium text-gray-800">{stage}</span>
                    </div>
                    <span className="text-xs text-gray-500">{candidates.length} candidate{candidates.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {candidates.slice(0, 10).map((c, i) => (
                      <div key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5">
                        <span className="font-medium">{c.name || 'Unnamed'}</span>
                        {(c.department || c.role) && (
                          <span className="text-gray-400 ml-1">&middot; {c.department || c.role}</span>
                        )}
                      </div>
                    ))}
                    {candidates.length > 10 && (
                      <div className="text-xs text-gray-400 px-2 py-1.5">+{candidates.length - 10} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPipeline;
